import { FormEvent, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthCard from './components/AuthCard';
import { showToastError, showToastSuccess } from './components/ToastNotifications';
import { AUTH_STORAGE_KEY } from './constants';
import {
  demoteSuperUser,
  getAdminUsers,
  promoteSuperUser,
} from './hooks/useUsers';
import { parseJwtPayload } from './utils/authTokens';
import { getErrorMessage } from './utils/requestErrors';

type SuperUserSearchResult = {
  id: number;
  email: string;
  name: string;
  active: boolean;
  verifiedEmail: boolean;
  isSuperUser: boolean;
  lockedUntil?: string | null;
};

const isMetrcEmail = (email: string): boolean =>
  String(email || '').trim().toLowerCase().endsWith('@metrc.com');

export default function SuperUserManagementPage() {
  const navigate = useNavigate();
  const token = window.localStorage.getItem(AUTH_STORAGE_KEY);
  const payload = token ? parseJwtPayload(token) : null;
  const isSuperUser = payload?.IsSuperUser === 'True' || payload?.IsSuperUser === true;

  const [email, setEmail] = useState('');
  const [results, setResults] = useState<SuperUserSearchResult[]>([]);
  const [status, setStatus] = useState<'idle' | 'searching' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [busyUserId, setBusyUserId] = useState<number | null>(null);

  const trimmedEmail = useMemo(() => email.trim(), [email]);
  const canSearch = isSuperUser && trimmedEmail.length > 0 && status !== 'searching';

  const searchUsers = async () => {
    if (!isSuperUser) {
      setStatus('error');
      setMessage('Only super users can manage super users.');
      return;
    }

    if (!trimmedEmail) {
      setStatus('error');
      setMessage('Enter at least part of an email address.');
      setResults([]);
      return;
    }

    setStatus('searching');
    setMessage('');

    try {
      const response = await getAdminUsers({
        email: trimmedEmail,
        page: 0,
        pageSize: 25,
      });

      const responseBody: any = response;
      const users = Array.isArray(responseBody)
        ? responseBody
        : Array.isArray(responseBody?.items)
          ? responseBody.items
          : Array.isArray(responseBody?.Items)
            ? responseBody.Items
            : [];

      const normalized = users.length
        ? users.map((user: any) => ({
            id: Number(user?.id ?? user?.Id ?? 0),
            email: String(user?.email ?? user?.Email ?? ''),
            name: String(user?.name ?? user?.Name ?? ''),
            active: Boolean(user?.active ?? user?.Active),
            verifiedEmail: Boolean(user?.verifiedEmail ?? user?.VerifiedEmail),
            isSuperUser: Boolean(user?.isSuperUser ?? user?.IsSuperUser),
            lockedUntil: user?.lockedUntil ?? user?.LockedUntil ?? null,
          }))
          .filter((user) => user.id > 0 && isMetrcEmail(user.email))
        : [];

      setResults(normalized);
      setStatus('success');
      setMessage(normalized.length ? `${normalized.length} user${normalized.length === 1 ? '' : 's'} found.` : 'No users found.');
    } catch (error) {
      setResults([]);
      setStatus('error');
      setMessage(getErrorMessage(error, 'Could not search users.'));
    }
  };

  const submitSearch = async (event: FormEvent) => {
    event.preventDefault();
    await searchUsers();
  };

  const updateUserSuperUser = async (user: SuperUserSearchResult, nextIsSuperUser: boolean) => {
    if (!isSuperUser) {
      showToastError('Only super users can manage super users.');
      return;
    }

    if (!user?.id) return;

    if (nextIsSuperUser && !isMetrcEmail(user.email)) {
      showToastError('Only @metrc.com users can be promoted to super user.');
      return;
    }

    setBusyUserId(user.id);
    setMessage('');
    try {
      if (nextIsSuperUser) {
        await promoteSuperUser(user.id);
      } else {
        await demoteSuperUser(user.id);
      }

      setResults((prev) =>
        prev.map((item) =>
          item.id === user.id ? { ...item, isSuperUser: nextIsSuperUser } : item,
        ),
      );
      setStatus('success');
      const action = nextIsSuperUser ? 'promoted to super user' : 'demoted from super user';
      setMessage(`${user.email} was ${action}.`);
      showToastSuccess(`${user.email} was ${action}.`);
    } catch (error) {
      const fallback = nextIsSuperUser
        ? 'Could not promote user to super user.'
        : 'Could not demote user from super user.';
      const errorMessage = getErrorMessage(error, fallback);
      setStatus('error');
      setMessage(errorMessage);
      showToastError(errorMessage);
    } finally {
      setBusyUserId(null);
    }
  };

  const renderUserResult = (user: SuperUserSearchResult) => {
    const canPromote = user.active && isMetrcEmail(user.email);

    return (
      <div
        key={user.id}
        className="card"
        style={{ padding: 12, display: 'grid', gap: 8 }}
        data-testid={`super-user-management-row-${user.id}`}
      >
        <div>
          <div data-testid={`super-user-management-name-${user.id}`} style={{ fontWeight: 800 }}>{user.name || 'Unnamed user'}</div>
          <div className="muted" data-testid={`super-user-management-email-${user.id}`} style={{ fontSize: 12 }}>{user.email}</div>
        </div>

        <div className="muted" data-testid={`super-user-management-state-${user.id}`} style={{ fontSize: 12 }}>
          Active: {user.active ? 'true' : 'false'} | Verified Email: {user.verifiedEmail ? 'true' : 'false'} | Super User: {user.isSuperUser ? 'true' : 'false'}
        </div>

        {!user.isSuperUser && !isMetrcEmail(user.email) && (
          <div className="muted" data-testid={`super-user-management-promotion-restricted-${user.id}`} style={{ fontSize: 12 }}>
            Only @metrc.com users can be promoted.
          </div>
        )}

        {isSuperUser && (
          user.isSuperUser ? (
            <button
              type="button"
              className="button secondary"
              disabled={busyUserId === user.id}
              onClick={() => updateUserSuperUser(user, false)}
              data-testid={`super-user-management-demote-${user.id}`}
            >
              {busyUserId === user.id ? 'Demoting...' : 'Demote Super User'}
            </button>
          ) : (
            <button
              type="button"
              className="button"
              disabled={busyUserId === user.id || !canPromote}
              onClick={() => updateUserSuperUser(user, true)}
              data-testid={`super-user-management-promote-${user.id}`}
            >
              {busyUserId === user.id ? 'Promoting...' : 'Promote Super User'}
            </button>
          )
        )}
      </div>
    );
  };

  return (
    <AuthCard
      pageTestId="page-super-user-management"
      cardTestId="super-user-management-card"
      ariaLabel="Manage super users"
      title="Retail ID"
      subtitle="Super user management"
    >
      <form onSubmit={submitSearch} style={{ display: 'grid', gap: 12 }}>
        {!isSuperUser && (
          <div className="alert error" role="alert" data-testid="super-user-management-forbidden">
            Only super users can manage super users.
          </div>
        )}

        {status === 'success' && message && (
          <div
            className="alert"
            role="status"
            data-testid="super-user-management-success"
            style={{
              background: '#f0fff4',
              border: '1px solid #b6f2c2',
              color: '#1f7a3a',
            }}
          >
            {message}
          </div>
        )}

        {status === 'error' && message && (
          <div className="alert error" role="alert" data-testid="super-user-management-error">
            {message}
          </div>
        )}

        <label className="field">
          <span>Email search</span>
          <input
            className="input"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="Search by email"
            disabled={!isSuperUser || status === 'searching'}
            data-testid="super-user-management-email"
          />
        </label>

        <button
          type="submit"
          className="button cta"
          style={{ width: '100%' }}
          disabled={!canSearch}
          data-testid="super-user-management-search"
        >
          {status === 'searching' ? 'Searching...' : 'Search Users'}
        </button>

        {results.length > 0 && (
          <div style={{ display: 'grid', gap: 8 }} data-testid="super-user-management-results">
            {results.map((user) => renderUserResult(user))}
          </div>
        )}

        <button
          type="button"
          className="button secondary"
          style={{ width: '100%' }}
          onClick={() => navigate('/internal')}
          data-testid="super-user-management-back"
        >
          Back to Internal
        </button>
      </form>
    </AuthCard>
  );
}
